/* eslint-disable @typescript-eslint/no-non-null-assertion */
export type LaravelRuleMessages = Partial<{
  required: string
  numeric: string
  email: string
  accepted: string
  minNumeric: (min: number) => string
  maxNumeric: (max: number) => string
  minString: (min: number) => string
  maxString: (max: number) => string
}>

const defaultMessages: LaravelRuleMessages = {
  required: 'Dit veld is verplicht',
  numeric: 'Dit veld moet een getal zijn',
  email: 'Dit veld moet een geldig e-mailadres zijn',
  accepted: 'Deze keuze moet worden geaccepteerd',
  minNumeric: min => `De waarde moet minimaal ${min} zijn`,
  maxNumeric: max => `De waarde mag maximaal ${max} zijn`,
  minString: min => `Dit veld moet minimaal ${min} tekens bevatten`,
  maxString: max => `Dit veld mag maximaal ${max} tekens bevatten`
}

function isEmpty (value: unknown): boolean {
  return value === undefined || value === null || value === ''
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toNumber (value: any): number {
  return Number(value)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function toStringValue (value: any): string {
  return String(value ?? '')
}

type ParsedRule = {
  name: string
  param?: string
}

function parseRules (ruleString: string): ParsedRule[] {
  return ruleString
    .split('|')
    .map(r => r.trim())
    .filter(Boolean)
    .map(rule => {
      const [name, param] = rule.split(':')

      return { name, param }
    })
}

type RuleContext = {
  hasNumeric: boolean
  hasString: boolean
  isNullable: boolean
}

function createRuleContext (parsedRules: ParsedRule[]): RuleContext {
  return {
    hasNumeric: parsedRules.some(r => r.name === 'numeric'),
    hasString: parsedRules.some(r => r.name === 'string'),
    isNullable: parsedRules.some(r => r.name === 'nullable')
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ValidatorFn = (value: any) => true | string

function createRequiredValidator (messages: LaravelRuleMessages): ValidatorFn {
  return value => {
    if (isEmpty(value)) {
      return messages.required!
    }

    return true
  }
}

function createNumericValidator (messages: LaravelRuleMessages): ValidatorFn {
  return value => {
    if (isEmpty(value)) return true
    const num = toNumber(value)

    if (Number.isNaN(num)) {
      return messages.numeric!
    }

    return true
  }
}

function createEmailValidator (messages: LaravelRuleMessages): ValidatorFn {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  return value => {
    if (isEmpty(value)) return true
    if (!emailRegex.test(toStringValue(value))) {
      return messages.email!
    }

    return true
  }
}
function createAcceptedValidator (messages: LaravelRuleMessages): ValidatorFn {
  return value => {
    if (isEmpty(value)) return true
    const v = String(value).toLowerCase()

    if (v === 'true' || v === '1' || v === 'yes' || v === 'on') {
      return true
    }

    return messages.accepted ?? 'Deze keuze moet worden geaccepteerd'
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function createMinValidator (
  rawParam: string | undefined,
  ctx: RuleContext,
  messages: LaravelRuleMessages
): ValidatorFn | null {
  const min = rawParam !== undefined ? Number(rawParam) : undefined

  if (min === undefined || Number.isNaN(min)) return null

  // If numeric mode is enabled (and no explicit string), do numeric check.
  if (ctx.hasNumeric && !ctx.hasString) {
    return value => {
      if (isEmpty(value)) return true
      const num = toNumber(value)

      if (Number.isNaN(num)) return messages.numeric!
      if (num < min) return messages.minNumeric!(min)

      return true
    }
  }

  // Otherwise string length
  return value => {
    if (isEmpty(value)) return true
    const str = toStringValue(value)

    if (str.length < min) return messages.minString!(min)

    return true
  }
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function createMaxValidator (
  rawParam: string | undefined,
  ctx: RuleContext,
  messages: LaravelRuleMessages
): ValidatorFn | null {
  const max = rawParam !== undefined ? Number(rawParam) : undefined

  if (max === undefined || Number.isNaN(max)) return null

  if (ctx.hasNumeric && !ctx.hasString) {
    return value => {
      if (isEmpty(value)) return true
      const num = toNumber(value)

      if (Number.isNaN(num)) return messages.numeric!
      if (num > max) return messages.maxNumeric!(max)

      return true
    }
  }

  return value => {
    if (isEmpty(value)) return true
    const str = toStringValue(value)

    if (str.length > max) return messages.maxString!(max)

    return true
  }
}

function buildValidators (
  parsedRules: ParsedRule[],
  ctx: RuleContext,
  messages: LaravelRuleMessages
): ValidatorFn[] {
  const validators: ValidatorFn[] = []

  for (const rule of parsedRules) {
    switch (rule.name) {
    case 'required': {
      validators.push(createRequiredValidator(messages))
      break
    }
    case 'numeric': {
      validators.push(createNumericValidator(messages))
      break
    }
    case 'string': {
      // used only to decide min/max mode – no direct validator
      break
    }
    case 'email': {
      validators.push(createEmailValidator(messages))
      break
    }
    case 'accepted': {
      validators.push(createAcceptedValidator(messages))
      break
    }
    case 'min': {
      const v = createMinValidator(rule.param, ctx, messages)

      if (v) validators.push(v)
      break
    }
    case 'max': {
      const v = createMaxValidator(rule.param, ctx, messages)

      if (v) validators.push(v)
      break
    }
    case 'nullable': {
      // handled globally in the final validate function
      break
    }
    default: {
      // ignore unsupported/unknown rules for now
      break
    }
    }
  }

  return validators
}

export function laravelValidate (
  ruleString: string,
  customMessages: LaravelRuleMessages = {}
) {
  const parsedRules = parseRules(ruleString)
  const ctx = createRuleContext(parsedRules)
  const messages = { ...defaultMessages, ...customMessages }
  const validators = buildValidators(parsedRules, ctx, messages)

  // This is the function you give to react-hook-form
  return (value: any) => {
    // nullable: empty is always OK (unless also required)
    if (ctx.isNullable && isEmpty(value)) {
      return true
    }

    for (const validator of validators) {
      const result = validator(value)

      if (result !== true) {
        return result
      }
    }

    return true
  }
}
